import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  WalletIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  BanknotesIcon,
  ChartBarIcon,
  DocumentTextIcon,
  UsersIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
} from '@heroicons/react/24/outline'
import { walletApi, Transaction } from '@/api/walletApi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

const categoryColors: Record<string, string> = {
  SCHOOL_FEES: 'bg-blue-100 text-blue-800',
  TRANSPORT_FEES: 'bg-purple-100 text-purple-800',
  LIBRARY_FEES: 'bg-indigo-100 text-indigo-800',
  EXAM_FEES: 'bg-pink-100 text-pink-800',
  UNIFORM: 'bg-orange-100 text-orange-800',
  BOOKS: 'bg-teal-100 text-teal-800',
  MEALS: 'bg-yellow-100 text-yellow-800',
  ACTIVITIES: 'bg-green-100 text-green-800',
  TOP_UP: 'bg-emerald-100 text-emerald-800',
  REFUND: 'bg-red-100 text-red-800',
  TRANSFER: 'bg-cyan-100 text-cyan-800',
  PENALTY: 'bg-rose-100 text-rose-800',
  DISCOUNT: 'bg-lime-100 text-lime-800',
  OTHER: 'bg-gray-100 text-gray-800',
}

function formatCurrency(amount: number, currency: string = 'KES') {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
  }).format(amount)
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function WalletDashboard() {
  const [period, setPeriod] = useState<'week' | 'month' | 'year'>('month')

  // Fetch wallet stats
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['walletStats'],
    queryFn: () => walletApi.getWalletStats(),
  })

  // Fetch all wallets
  const { data: walletsData, isLoading: walletsLoading } = useQuery({
    queryKey: ['allWallets'],
    queryFn: () => walletApi.getAllWallets({ limit: 10 }),
  })

  // Fetch recent transactions
  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: ['recentTransactions'],
    queryFn: () => walletApi.getAllTransactions({ limit: 10 }),
  })

  const isLoading = statsLoading || walletsLoading || transactionsLoading

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const stats = walletsData?.data?.stats || {
    totalBalance: 0,
    avgBalance: 0,
    activeWallets: 0,
    totalWallets: 0,
  }

  const transactionSummary = transactionsData?.data?.summary || {
    totalCredits: 0,
    totalDebits: 0,
    creditCount: 0,
    debitCount: 0,
  }

  const recentTransactions = transactionsData?.data?.transactions || []
  const topWallets = walletsData?.data?.wallets?.slice(0, 5) || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wallet Management</h1>
          <p className="text-gray-600">Monitor and manage student and parent wallets</p>
        </div>
        <div className="flex gap-2">
          {(['week', 'month', 'year'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                period === p
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Balance</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(stats.totalBalance)}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <BanknotesIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-500">{stats.totalWallets} total wallets</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Credits</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(transactionSummary.totalCredits)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <ArrowTrendingUpIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-500">{transactionSummary.creditCount} transactions</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Debits</p>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(transactionSummary.totalDebits)}
              </p>
            </div>
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <ArrowTrendingDownIcon className="w-6 h-6 text-red-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-500">{transactionSummary.debitCount} transactions</span>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Wallets</p>
              <p className="text-2xl font-bold text-gray-900">{stats.activeWallets}</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <UsersIcon className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-500">
              Avg: {formatCurrency(stats.avgBalance)}
            </span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <a
          href="/schoolAdmin/wallet/credit"
          className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl hover:from-green-600 hover:to-green-700 transition-all shadow-sm"
        >
          <ArrowUpIcon className="w-6 h-6" />
          <div>
            <p className="font-semibold">Credit Wallet</p>
            <p className="text-sm text-green-100">Add funds to wallet</p>
          </div>
        </a>

        <a
          href="/schoolAdmin/wallet/debit"
          className="flex items-center gap-3 p-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all shadow-sm"
        >
          <ArrowDownIcon className="w-6 h-6" />
          <div>
            <p className="font-semibold">Debit Wallet</p>
            <p className="text-sm text-red-100">Deduct from wallet</p>
          </div>
        </a>

        <a
          href="/schoolAdmin/wallet/transactions"
          className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm"
        >
          <ChartBarIcon className="w-6 h-6" />
          <div>
            <p className="font-semibold">All Transactions</p>
            <p className="text-sm text-blue-100">View transaction history</p>
          </div>
        </a>

        <a
          href="/schoolAdmin/wallet/invoices"
          className="flex items-center gap-3 p-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-purple-700 transition-all shadow-sm"
        >
          <DocumentTextIcon className="w-6 h-6" />
          <div>
            <p className="font-semibold">Invoices</p>
            <p className="text-sm text-purple-100">Manage fee invoices</p>
          </div>
        </a>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transactions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Recent Transactions</h2>
              <a
                href="/schoolAdmin/wallet/transactions"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                View all
              </a>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {recentTransactions.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No transactions yet</div>
            ) : (
              recentTransactions.map((transaction: Transaction) => {
                const user = typeof transaction.userId === 'object' ? transaction.userId : null
                return (
                  <div key={transaction._id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            transaction.type === 'CREDIT'
                              ? 'bg-green-100 text-green-600'
                              : 'bg-red-100 text-red-600'
                          }`}
                        >
                          {transaction.type === 'CREDIT' ? (
                            <ArrowUpIcon className="w-5 h-5" />
                          ) : (
                            <ArrowDownIcon className="w-5 h-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {user ? `${user.firstName} ${user.lastName}` : 'Unknown User'}
                          </p>
                          <p className="text-sm text-gray-500">{transaction.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p
                          className={`font-semibold ${
                            transaction.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {transaction.type === 'CREDIT' ? '+' : '-'}
                          {formatCurrency(transaction.amount)}
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(transaction.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Top Wallets */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Top Wallet Balances</h2>
              <a
                href="/schoolAdmin/wallet/all"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                View all
              </a>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {topWallets.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No wallets yet</div>
            ) : (
              topWallets.map((wallet: any, index: number) => (
                <div key={wallet._id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 text-white flex items-center justify-center font-semibold text-sm">
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {wallet.userId?.firstName} {wallet.userId?.lastName}
                        </p>
                        <p className="text-sm text-gray-500">{wallet.userId?.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        {formatCurrency(wallet.balance, wallet.currency)}
                      </p>
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                          wallet.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {wallet.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Category Breakdown */}
      {statsData?.data?.topCategories && statsData.data.topCategories.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Top Spending Categories</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {statsData.data.topCategories.map((cat: any) => (
              <div key={cat._id} className="text-center p-4 rounded-lg bg-gray-50">
                <span
                  className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${
                    categoryColors[cat._id] || categoryColors.OTHER
                  }`}
                >
                  {cat._id.replace('_', ' ')}
                </span>
                <p className="mt-2 text-lg font-semibold text-gray-900">
                  {formatCurrency(cat.total)}
                </p>
                <p className="text-sm text-gray-500">{cat.count} transactions</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
